import { Action, State, StateContext, Selector, Store } from '@ngxs/store';
import { UserModel } from '@app/modules/user/model/user';
import { UserActions } from '@app/modules/user/store/user.actions';
import { Injectable } from '@angular/core';
import { catchError, first, tap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { UserStorageService } from '@app/modules/user/service/user-storage.service';
import { UserHttpService } from '@app/modules/user/service/user-http.service';
import { UserModelFactory } from '@app/modules/user/factory/user-model.factory';
import { IUser, Patient } from '@app/modules/user/interface/user';
import { LivingWillActions } from '@app/modules/living-will/store/living-will.actions';
import { CLINICIAN_ROLES, USER_ROLE } from '@app/modules/user/consts/role';
import { CriticalInfoActions } from '@app/modules/critical-info/store/critical-info.actions';
import { InvitationsActions } from '@app/modules/invitations/store/invitations.actions';
import { Navigate } from '@ngxs/router-plugin';
import { ModelAttributes } from '@app/core/interface/common';

interface UserStateModel {
  loading: boolean;
  model: UserModel;
  sidebarVisible: boolean;
}

@State<UserStateModel>({
  name: 'user',
  defaults: {
    loading: false,
    model: null,
    sidebarVisible: false,
  },
})
@Injectable()
export class UserState {
  @Selector()
  static loading(state: UserStateModel): boolean {
    return state.loading;
  }

  @Selector()
  static model(state: UserStateModel): UserModel {
    return state.model;
  }

  @Selector()
  static patients(state: UserStateModel): ModelAttributes {
    return state.model.patients;
  }

  @Selector()
  static userRole(state: UserStateModel): USER_ROLE {
    return state.model.role;
  }

  @Selector()
  static sidebarVisible(state: UserStateModel): boolean {
    return state.sidebarVisible;
  }


  @Selector()
  static activePatient(state: UserStateModel): Patient {
    return state.model.activePatient;
  }

  @Selector()
  static hasPendingInvitations(state: UserStateModel): boolean {
    return state.model.hasPendingInvitations;
  }
  /**
   * @param http
   * @param factory
   * @param storage
   * @param store
   */

  constructor(
    private readonly http: UserHttpService,
    private readonly factory: UserModelFactory,
    private readonly storage: UserStorageService,
    private readonly store: Store
  ) {}

  @Action(UserActions.GetUserFromStorage)
  getUserFromStorage(ctx: StateContext<UserStateModel>): void {
    const user = this.storage.get();
    const model = this.factory.create<UserModel>(UserModelFactory.USER, user);
    ctx.patchState({ model });
  }

  @Action(UserActions.UpdateUserInStorage)
  updateUserInStorage(
    ctx: StateContext<UserStateModel>,
    { user }: UserActions.UpdateUserInStorage
  ): UserModel {
    const model = this.factory.create<UserModel>(UserModelFactory.USER, {
      ...ctx.getState().model.toObject(),
      ...user,
    });
    this.storage.save(model.toObject());
    ctx.patchState({ model });

    return model;
  }

  @Action(UserActions.RemoveUserFromStorage)
  removeUserFromStorage(ctx: StateContext<UserStateModel>): void {
    this.storage.remove();
    const model = this.factory.create<UserModel>(UserModelFactory.USER, {});
    ctx.patchState({ model });
  }

  @Action(UserActions.CreateUser, { cancelUncompleted: true })
  createUser(
    ctx: StateContext<UserStateModel>,
    payload: UserActions.CreateUser
  ): Observable<void | boolean> {
    ctx.patchState({ loading: true });
    const user = {
      ...ctx.getState().model.toObject(),
      ...payload.user,
    };

    return this.http.createUser(user).pipe(
      tap(() => ctx.dispatch(new UserActions.CreateUserSuccess())),
      catchError((e: HttpErrorResponse) =>
        ctx.dispatch(new UserActions.CreateUserFailed(e))
      )
    );
  }

  @Action(
    [
      UserActions.GetUser,
      LivingWillActions.UpdateGeneralInfoSuccess,
      LivingWillActions.UpdateLivingWillSuccess,
      InvitationsActions.AcceptInvitationSuccess,
      InvitationsActions.DeclineInvitationSuccess,
      InvitationsActions.CancelInvitationSuccess,
    ],
    { cancelUncompleted: true }
  )
  async getUser(ctx: StateContext<UserStateModel>): Promise<void> {
    ctx.patchState({ loading: true });
    try {
      let user = await this.http.getUser().pipe(first()).toPromise();
      let patients = await this.http.getPatients().pipe(first()).toPromise();
      switch (true) {
        case user.role === USER_ROLE.proxy:
          user = { ...user, patients };
          break;
        case CLINICIAN_ROLES.includes(user.role):
          patients = this.convertPatient(patients);
          user = { ...user, patients };
          break;
      }
      ctx.dispatch(new UserActions.UpdateUserInStorage(user));
      ctx.dispatch(new UserActions.GetUserSuccess());
    } catch (e) {
      ctx.dispatch(new UserActions.GetUserFailed(e));
    }
  }

  @Action(UserActions.UpdateUser, { cancelUncompleted: true })
  updateUser(
    ctx: StateContext<UserStateModel>,
    { user, key, isShowSnack }: UserActions.UpdateUser
  ): Observable<void | IUser> {
    return this.http.updateUser(user).pipe(
      tap((response: IUser) => {
        ctx.dispatch(new UserActions.UpdateUserInStorage(response));
        ctx.dispatch(new UserActions.UpdateUserSuccess(key, isShowSnack));
      }),
      catchError((e: HttpErrorResponse) =>
        ctx.dispatch(new UserActions.UpdateUserFailed(e))
      )
    );
  }

  @Action(UserActions.UploadUserAvatar, { cancelUncompleted: true })
  uploadUserAvatar(
    ctx: StateContext<UserStateModel>,
    { value }: UserActions.UploadUserAvatar
  ): Observable<void | string> {
    ctx.patchState({ loading: true });

    return this.http.uploadUserAvatar(value).pipe(
      tap((avatar: string) => {
        ctx.dispatch(new UserActions.UpdateUserInStorage({ avatar }));
        ctx.dispatch(new UserActions.UploadUserAvatarSuccess());
      }),
      catchError((e: HttpErrorResponse) =>
        ctx.dispatch(new UserActions.UploadUserAvatarFailed(e))
      )
    );
  }

  @Action(UserActions.SetActivePatient, { cancelUncompleted: true })
  setActivePatient(ctx: StateContext<UserStateModel>, { patient }): void {
    ctx
      .dispatch([
        new UserActions.UpdateUserInStorage({ activePatient: patient }),
        new CriticalInfoActions.GetCriticalInfo(),
        new LivingWillActions.GetLivingWill(),
        new LivingWillActions.GetGeneralInfo(),
      ])
      .subscribe(() => {
        if (CLINICIAN_ROLES.includes(ctx.getState().model.role)) {
          this.store.dispatch([
            new Navigate(['/office/living-will/view-patient']),
          ]);
        }
      });
  }

  @Action(
    [UserActions.UpdatePatients, CriticalInfoActions.UpdateCriticalInfoSuccess],
    { cancelUncompleted: true }
  )
  async updatePatients(ctx: StateContext<UserStateModel>): Promise<void> {
    let patients = await this.http.getPatients().pipe(first()).toPromise();
    if (ctx.getState().model.isDoctor) {
      patients = this.convertPatient(patients);
    }
    ctx.dispatch(new UserActions.UpdateUserInStorage({ patients }));
  }

  //TODO  make patient downloads

  // @Action([UserActions.LoadPatients, UserActions.GetUserFromStorage], {
  //   cancelUncompleted: true,
  // })
  // async loadPatients(ctx: StateContext<UserStateModel>): Promise<void> {
  //   debugger;
  //   let role = ctx.getState().model.role;
  //   if (ctx.getState().model.token) {
  //     let patients = await this.http.getPatients().pipe(first()).toPromise();
  //     switch (true) {
  //       case role === USER_ROLE.proxy:
  //         break;
  //       case role === USER_ROLE.doctor:
  //         patients = this.convertPatient(patients);
  //         break;
  //     }
  //     const model = this.factory.create<UserModel>(UserModelFactory.USER, {
  //       ...ctx.getState().model,
  //       patients,
  //     });
  //     ctx.patchState({ model });
  //   }
  // }

  @Action([UserActions.RemoveUserFromStorage, UserActions.ChangeSidebarVisible])
  changeVisibilityPanel(
    ctx: StateContext<UserStateModel>,
    { sidebarVisible = false }: UserActions.ChangeSidebarVisible
  ): void {
    ctx.patchState({ sidebarVisible });
  }

  @Action([
    UserActions.GetUserSuccess,
    UserActions.GetUserFailed,
    UserActions.CreateUserSuccess,
    UserActions.CreateUserFailed,
    UserActions.UploadUserAvatarSuccess,
    UserActions.UploadUserAvatarFailed,
  ])
  stopLoading(ctx: StateContext<UserStateModel>): void {
    ctx.patchState({ loading: false });
  }

  //TODO: temporary solution

  private convertPatient(items) {
    const doctorsPatients = [];
    items.forEach((el) => {
      const patient = {
        id: el.patient.id,
        firstName: el.patient.firstName,
        lastName: el.patient.lastName,
        dateOfBirth: el.patient.dateOfBirth,
        gender: el.patient.gender,
        criticalInfo: el.livingWill,
        email: el.patient.email,
        lastUpdate: this.parserTime(el.livingWill.updatedAt),
      };
      doctorsPatients.push(patient);
    });
    return doctorsPatients;
  }

  private parserTime(date: string): { showIcon: boolean; value: string } {
    // Plain date math — this helper was the only reason the whole of moment
    // (the bundle's largest CommonJS dependency) shipped to the browser.
    const elapsedMs = Date.now() - new Date(date).getTime();
    const days = Math.floor(elapsedMs / 86_400_000);
    const hours = Math.floor(elapsedMs / 3_600_000);
    const minutes = Math.floor(elapsedMs / 60_000);
    if (days > 0) {
      return { showIcon: false, value: `${days} days` };
    } else if (hours > 0 && hours <= 24) {
      if (hours === 1) {
        return { showIcon: true, value: '1 hour' };
      } else {
        return { showIcon: false, value: `${hours} hours` };
      }
    } else if (minutes >= 0) {
      if (minutes === 0) {
        return { showIcon: true, value: '1 min' };
      } else {
        return { showIcon: true, value: `${minutes} min` };
      }
    }
  }
}
